import { LoadingFull } from 'components/Loading';
import NotFoundPage from 'features/NotFound';
import { first, sum } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { momentTimezone } from 'utils/time';
import SuccessStep, { IAppointmentSuccess } from '../BookingOnlineV2/SuccessStep';
import aptSelectors from '../services/selectors';
import Button from './Button';
import fail_img from './fail.png';
import success_img from './success.png';

const BookingDepositTransactionUI = () => {
  const { shop_id = '' } = useParams();
  const state = aptSelectors.depositTrans();

  const onBookAnother = () => {
    window.location.href = `${location.origin}/store/${shop_id}/booking-v2`;
  };

  const aptSuccess = useMemo(() => {
    const aptData = state.data?.appointments || [];
    const successData = first(aptData);
    const startDate = successData?.startTime ? momentTimezone(successData?.startTime || '') : moment();
    const duration = sum(aptData?.map(o => sum(o.services?.map(s => s.duration))));
    const endTime = startDate.clone().add(duration, 'minutes').format('hh:mm A');
    return {
      qrCode: successData?.qrCode || '',
      customerName: successData?.customerName,
      customerPhone: successData?.customerPhone,
      date: startDate.format('MM-DD-YYYY'),
      items: aptData,
      startTime: startDate.format('hh:mm A'),
      endTime: endTime,
      note: successData?.note || '',
    } as IAppointmentSuccess;
  }, [state.data]);

  if (state.loading) return <LoadingFull />;

  if (!state.data) return <NotFoundPage />;

  if (state.data.status === 'ERROR') {
    return (
      <Page>
        <Container>
          <div className="box-container error-container">
            <img src={fail_img} alt="fail_img" style={{ width: '80%', height: 'auto' }} />
            <div className="error-title">{state.data.message}</div>
            <div className="error-description">Please check your payment details or try a different payment method. Your booking will not be secured until the deposit is successfully paid.</div>
          </div>
        </Container>
        <Footer>
          <Button onClick={onBookAnother}>TRY AGAIN</Button>
        </Footer>
      </Page>
    );
  }

  return (
    <Page>
      <Container>
        <div className="box-container success-container">
          <img src={success_img} alt="success_img" style={{ width: '40px', height: '40px' }} />
          <div className="success-title">Booking Success!</div>
          <div className="success-description">
            <span>Deposited: <b>{formatCurrency(state.data.amount)}</b></span>
          </div>

          <div className="dash-border"></div>
          <SuccessStep deposit value={aptSuccess} />
        </div>
      </Container>
      <Footer>
        <Button onClick={onBookAnother}>BOOK ANOTHER APPOINTMENT</Button>
      </Footer>
    </Page>
  );
};
const BookingDepositTransaction = () => {
  useEffect(() => {
    document.body.style.background = '#232F3E';
  }, []);
  return (
    <Wrapper>
      <div className="box-container-responsive">
        <BookingDepositTransactionUI />
      </div>
    </Wrapper>
  );
};
export default BookingDepositTransaction;
const Wrapper = styled.div`
  min-height: 100vh;
  background: #232F3E;
  .box-container-responsive {
    align-self: stretch;
    background: #F5F5F5;
    height: 100%;
    position: relative;
    max-width: 476px;
    margin: 0 auto;
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.15);

    &::-webkit-scrollbar {
      width: 0px;
    }
  }

  @media only screen and (max-width: 500px) {
    .box-container-responsive {
      width: 100%;
      max-width: unset;
    } 
  }
`;



const Page = styled.div`
background: #F5F5F5;
height: 100%;
display: flex;
flex-direction: column;

.ant-form-item {
  align-self: stretch;
  .ant-form-item-row {
    position: relative;
    padding-top: 12px;
  }

  .ant-form-item-label {
    padding: 0;
    z-index: 2;
    position: absolute;
    top: 0px;
    left: 8px;
    label {
      height: unset;
      z-index: 9;
      background: #fff;
      &::before {
        display: none !important;
      }

      padding: 0 2px;

      color: #1D2129;
      font-family: Poppins;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 6px;

    }
  }
  .ant-form-item-control {
    z-index: 1;
  }

  &:focus-within {
    .ant-form-item-label label {
      color: #2463EB;
    }
  }

  .ant-form-item-control-input input {
    font-size: 18px;
    text-align: center;
    height: 56px;

    color: #1D1B20;
    &::placeholder {
      color: #64748B;
    }

    &:-ms-input-placeholder {
      color: #64748B;
    }

    &::-ms-input-placeholder {
      color: #64748B;
    }
  }
}
`;

const Container = styled.div`
min-height: 90vh;
padding: 16px 16px;
padding-top: 16px;
padding-bottom: 24px;

.box-container {
  background: #FFF;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15);
  display:flex;
  flex-direction: column;
  align-items: center;
  border-radius: 12px;
  padding: 16px 4px;
  padding-bottom: 32px;
  &.error-container {
    .error-title {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  
    .error-description {
      align-self: stretch;
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 28px; /* 175% */
    }
  }

  &.success-container {
    .success-title {
      color: #0C865D;
      text-align: center;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  
    .success-description {
      display: flex;
      padding: 0 8px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 4px;
      background: #0C865D;
      span {
        color: #FFF;
        text-align: center;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;

        b {
          color: #FFF;
          font-family: Poppins;
          font-size: 24px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }

      }
    }


  }

  .dash-border {
    border-bottom: 1px dashed #CCD4DC;
    margin: 8px 0;
    align-self: stretch;
    height: 1px;
  }


  .qr-group {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;

    

  }
}
`;

const Footer = styled.div`
position: sticky;
bottom: 0;
z-index: 99;
display: flex;
padding: 12px 16px 0 16px;
padding-bottom: 12px;
align-items: center;
gap: 12px;
background: #fff;
box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.15);
button {
  flex: 1;
}
`;
